/**
 * Centralized hook for validation errors management
 * Simplified - just read from Redux, no complex caching
 */

import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { hasValidationErrors, hasTaricJsonErrors } from '../utils/errorCheckUtils';

/**
 * Get validation errors from active workflow session state
 */
export const useValidationErrors = () => {
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  
  const validationErrors = useSelector((state) => {
    const sessionId = state.workflowSessions?.activeSessionId;
    if (!sessionId) return [];
    const errors = state.workflowSessions?.sessions?.[sessionId]?.state?.validationErrors;
    return Array.isArray(errors) ? errors : [];
  });

  const hasErrors = useMemo(() => hasValidationErrors(validationErrors), [validationErrors]);

  return {
    validationErrors,
    hasErrors,
    activeWorkflowSessionId,
  };
};

/**
 * Get TARIC JSON errors from active workflow session state
 */
export const useTaricJsonErrors = () => {
  // Select only taricJsonErrors to avoid unnecessary re-renders
  const taricJsonErrorsRaw = useSelector((state) => {
    const sessionId = state.workflowSessions?.activeSessionId;
    if (!sessionId) return null;
    return state.workflowSessions?.sessions?.[sessionId]?.state?.taricJsonErrors;
  });

  const taricJsonErrors = useMemo(() => {
    return hasTaricJsonErrors(taricJsonErrorsRaw) ? taricJsonErrorsRaw : null;
  }, [taricJsonErrorsRaw]);

  const hasErrors = useMemo(() => {
    return hasTaricJsonErrors(taricJsonErrors);
  }, [taricJsonErrors]);

  return {
    taricJsonErrors,
    hasErrors,
  };
};

/**
 * Combined hook for both validation and TARIC JSON errors
 */
export const useAllErrors = () => {
  const { validationErrors, hasErrors: hasValidationErrors, activeWorkflowSessionId } = useValidationErrors();
  const { taricJsonErrors, hasErrors: hasTaricErrors } = useTaricJsonErrors();
  
  const hasAnyErrors = useMemo(() => {
    return hasValidationErrors || hasTaricErrors;
  }, [hasValidationErrors, hasTaricErrors]);

  return {
    validationErrors,
    taricJsonErrors,
    hasValidationErrors,
    hasTaricErrors,
    hasAnyErrors,
    activeWorkflowSessionId,
  };
};

